// Client-side logo downscale (perf: the tenant logo renders in a fixed ~50pt box
// on the quote PDF, so anything above ~150px is oversampled and inflates the
// worker's PDF-generate CPU — the job-pdf 1102 cause). Downscale to a 150px max
// dimension at UPLOAD so every PDF generation embeds a tiny image. Canvas-based,
// runs in the browser; zero worker CPU.

import { LOGO_MAX_DIM } from '@gutterinv/shared';
export { LOGO_MAX_DIM };

/** Aspect-preserving target dims so the longest side ≤ max. Already-small images
 *  pass through unchanged (never upscale). Pure — unit-tested (canvas isn't). */
export function fitDimensions(
  w: number,
  h: number,
  max: number = LOGO_MAX_DIM,
): { width: number; height: number; scaled: boolean } {
  const longest = Math.max(w, h);
  if (longest <= max || longest === 0) return { width: w, height: h, scaled: false };
  const scale = max / longest;
  // Round to ≥1 so a very wide/tall logo never collapses a dimension to 0.
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
    scaled: true,
  };
}

/** Downscale an uploaded image File/Blob to ≤150px longest side, re-encoded as
 *  PNG (preserves alpha for transparent logos). Returns the ORIGINAL blob
 *  unchanged when it's already within the cap or can't be decoded (the worker
 *  guard is the backstop). Browser-only (needs Image + canvas). */
export async function downscaleLogo(
  file: Blob,
  max: number = LOGO_MAX_DIM,
): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const fit = fitDimensions(img.naturalWidth, img.naturalHeight, max);
    if (!fit.scaled) return file; // already small enough — upload as-is
    const canvas = document.createElement('canvas');
    canvas.width = fit.width;
    canvas.height = fit.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file; // no 2d context → let the worker guard catch it
    ctx.drawImage(img, 0, 0, fit.width, fit.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png'),
    );
    return blob ?? file;
  } catch {
    return file; // decode failed → upload original; worker guard backstops
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}
